<template>
    <div class="common-form-wrapper" :class="{ affix: isFixed }">
        <div class="wrapper">
            <span v-if="$v.value.name.$invalid" class="tooltiptext">{{
                errorDisplay($v.value.name, { val: MAX_NAME_LENGTH })
            }}</span>
            <div class="input-with-insidelabel" :class="{ tooltiperror: $v.value.name.$invalid }">
                <input
                    id="forAT_AccessGroup-Name"
                    ref="groupName"
                    class="time-input"
                    :value="value.name"
                    @input="update('name', $event.target.value) && $v.value.name.$touch()"
                />
                <label for="forAT_AccessGroup-Name">{{
                    $t('wanderManagement.accessGroupsTab.accessGroup.name')
                }}</label>
            </div>
        </div>
        <div class="wrapper">
            <span v-if="$v.value.comment.$invalid" class="tooltiptext">{{
                errorDisplay($v.value.comment, { val: MAX_COMMENT_LENGTH })
            }}</span>
            <div
                class="textarea-with-insidelabel has-5-rows"
                :class="{ tooltiperror: $v.value.comment.$invalid }"
            >
                <textarea
                    id="forAT_AccessGroup-Comment"
                    :value="value.comment"
                    @input="update('comment', $event.target.value) && $v.value.comment.$touch()"
                >
                </textarea>
                <label for="forAT_AccessGroup-Comment">{{
                    $t('wanderManagement.accessGroupsTab.accessGroup.comments')
                }}</label>
            </div>
        </div>
        <div class="multiselect-wrapper">
            <Multiselect
                id="forAT_AccessGroup-AlwaysAccess"
                ref="alwaysAccessMultiselect"
                :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.alwaysAccess')"
                :options="updatedAlwaysAccessOptions"
                :value="value.always_access"
                :multiple="true"
                :select-label="emptyLabel"
                :deselect-label="selected"
                :selected-label="emptyLabel"
                :close-on-select="false"
                label="name"
                track-by="name"
                @search-change="updateAlwaysAccessQuery"
                @input="update('always_access', $event)"
            >
                <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                <template slot="option" slot-scope="props">
                    <span class="option_title" :title="props.option.title">{{ props.option.name }}</span>
                </template>
                <template slot="tag" slot-scope="props">
                    <span class="multiselect__tag">
                        <span :title="props.option.title">{{ props.option.name }}</span>
                        <i
                            aria-hidden="true"
                            tabindex="1"
                            class="multiselect__tag-icon"
                            @keydown.enter.prevent="props.remove(props.option)"
                            @mousedown.prevent="props.remove(props.option)"
                        ></i>
                    </span>
                </template>
            </Multiselect>
        </div>
        <div class="multiselect-wrapper">
            <Multiselect
                id="forAT_AccessGroup-ConditionalAccess"
                ref="conditionalAccessMultiselect"
                :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.conditionalAccess')"
                :options="updatedConditionalAccessOptions"
                :value="value.conditional_access"
                :multiple="true"
                :select-label="emptyLabel"
                :deselect-label="selected"
                :selected-label="emptyLabel"
                :close-on-select="false"
                label="name"
                track-by="name"
                @search-change="updateConditionalAccessQuery"
                @input="update('conditional_access', $event)"
            >
                <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
                <template slot="option" slot-scope="props">
                    <span class="option_title" :title="props.option.title">{{ props.option.name }}</span>
                </template>
                <template slot="tag" slot-scope="props">
                    <span class="multiselect__tag">
                        <span :title="props.option.title">{{ props.option.name }}</span>
                        <i
                            aria-hidden="true"
                            tabindex="1"
                            class="multiselect__tag-icon"
                            @keydown.enter.prevent="props.remove(props.option)"
                            @mousedown.prevent="props.remove(props.option)"
                        ></i>
                    </span>
                </template>
            </Multiselect>
        </div>
        <div class="multiselect-wrapper">
            <Multiselect
                id="forAT_AccessGroup-ExitDetection"
                ref="exitDetectionMultiselect"
                :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.exitDetection')"
                :options="exitDetectionOptions"
                :value="value.exit_detection"
                :multiple="true"
                :select-label="emptyLabel"
                :deselect-label="selected"
                :selected-label="emptyLabel"
                :close-on-select="false"
                label="name"
                track-by="name"
                @search-change="updateExitDetectionQuery"
                @input="update('exit_detection', $event)"
            >
                <span slot="noResult">{{ $t('common.noSearchResults') }}</span>
            </Multiselect>
        </div>
        <div class="alarm-wrapper">
            <Hide v-model="wanderVisibility">
                <h4>{{ $t('wanderManagement.accessGroupsTab.accessGroup.wanderAlarm') }}</h4>
            </Hide>
            <div
                v-if="!!value.wander_alarm && value.wander_alarm.length > 0"
                v-show="wanderVisibility"
                :class="['search-alarm', { 'active-search': wanderKeyword }]"
            >
                <i class="icon-search"></i>
                <input
                    id="forAT_search-WanderAlarm"
                    v-model="wanderKeyword"
                    class="search-input"
                    maxlength="30"
                    :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.search')"
                />
                <button @click="wanderKeyword = ''"><i class="icon-close"></i></button>
            </div>
            <div
                v-for="(location, index) in wanderAlarmsFound"
                v-show="wanderVisibility"
                :key="index"
                class="alarm-wrapper-pattern"
            >
                <SelectBox
                    :id="'forAT_AccessGroup-WanderAlarm' + index"
                    :style-class="'alarm-select'"
                    :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.location')"
                    :options="locationOptions('wander', index, wanderAlarmOptions)"
                    :value="location.value"
                    @input="updateAlarm('wander', location, $event)"
                />
                <div class="wander-duration-wrapper">
                    <span v-if="!isDelayDurationValid('wander', index, 'duration')" class="tooltiptext">
                        {{
                            errorDisplay($v.wanderAlarmsFound.$each[index].duration, {
                                minValue: MIN_DELAY_DURATION,
                                maxValue: MAX_DELAY_DURATION,
                            })
                        }}
                    </span>
                    <div
                        :class="[
                            'input-with-insidelabel',
                            { tooltiperror: !isDelayDurationValid('wander', index, 'duration') },
                        ]"
                    >
                        <input
                            id="listenWanderDuration"
                            class="time-input"
                            :value="location.duration"
                            :placeholder="infinityPlaceholder"
                            :disabled="!location.value"
                            @input="
                                updateAlarm('wander', location, $event.target.value, 'duration') &&
                                    $v.wanderAlarmsFound.$each[index].duration.$touch()
                            "
                        />
                        <label for="listenWanderDuration">{{
                            $t('wanderManagement.accessGroupsTab.accessGroup.duration')
                        }}</label>
                    </div>
                </div>
                <button
                    v-if="location.value"
                    class="btn-trash alarm-delete"
                    @click.prevent="removeAlarm('wander', location)"
                >
                    <i class="icon-trash"></i>
                </button>
            </div>
            <h3 v-if="wanderKeyword && !wanderAlarmsFound.length">
                {{ $t('common.searchNoResult', { val: wanderKeyword }) }}
            </h3>
        </div>
        <div class="alarm-wrapper">
            <Hide v-model="loiterVisibility">
                <h4>{{ $t('wanderManagement.accessGroupsTab.accessGroup.loiterAlarm') }}</h4>
            </Hide>
            <div
                v-if="!!value.loiter_alarm && value.loiter_alarm.length > 0"
                v-show="loiterVisibility"
                :class="['search-alarm', { 'active-search': loiterKeyword }]"
            >
                <i class="icon-search"></i>
                <input
                    id="forAT_searchLoiterAlarm"
                    v-model="loiterKeyword"
                    class="search-input"
                    maxlength="30"
                    :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.search')"
                />
                <button @click="loiterKeyword = ''"><i class="icon-close"></i></button>
            </div>
            <div
                v-for="(location, index) in loiterAlarmsFound"
                v-show="loiterVisibility"
                :key="index"
                class="alarm-wrapper-pattern"
            >
                <SelectBox
                    :id="'forAT_AccessGroup-LoiterAlarm' + index"
                    :style-class="'alarm-select'"
                    :placeholder="$t('wanderManagement.accessGroupsTab.accessGroup.location')"
                    :options="locationOptions('loiter', index, loiterAlarmOptions)"
                    :value="location.value"
                    @input="updateAlarm('loiter', location, $event)"
                />
                <div class="loiter-delay-duration-wrapper">
                    <span v-if="!isDelayDurationValid('loiter', index, 'delay')" class="tooltiptext">
                        {{
                            errorDisplay($v.loiterAlarmsFound.$each[index].delay, {
                                minValue: MIN_DELAY_DURATION,
                                maxValue: MAX_DELAY_DURATION,
                            })
                        }}
                    </span>
                    <div
                        :class="[
                            'input-with-insidelabel',
                            { tooltiperror: !isDelayDurationValid('loiter', index, 'delay') },
                        ]"
                    >
                        <input
                            id="listenDelay"
                            class="time-input"
                            :value="location.delay"
                            :disabled="!location.value"
                            @input="updateAlarm('loiter', location, $event.target.value, 'delay')"
                        />
                        <label for="listenDelay">{{
                            $t('wanderManagement.accessGroupsTab.accessGroup.delay')
                        }}</label>
                    </div>
                </div>
                <div class="loiter-delay-duration-wrapper">
                    <span v-if="!isDelayDurationValid('loiter', index, 'duration')" class="tooltiptext">
                        {{
                            errorDisplay($v.loiterAlarmsFound.$each[index].duration, {
                                minValue: MIN_DELAY_DURATION,
                                maxValue: MAX_DELAY_DURATION,
                            })
                        }}
                    </span>
                    <div
                        :class="[
                            'input-with-insidelabel',
                            { tooltiperror: !isDelayDurationValid('loiter', index, 'duration') },
                        ]"
                    >
                        <input
                            id="listenLoiterDuration"
                            class="time-input"
                            :value="location.duration"
                            :placeholder="infinityPlaceholder"
                            :disabled="!location.value"
                            @input="updateAlarm('loiter', location, $event.target.value, 'duration')"
                        />
                        <label for="listenLoiterDuration">{{
                            $t('wanderManagement.accessGroupsTab.accessGroup.duration')
                        }}</label>
                    </div>
                </div>
                <button
                    v-if="location.value"
                    class="btn-trash alarm-delete"
                    @click.prevent="removeAlarm('loiter', location)"
                >
                    <i class="icon-trash"></i>
                </button>
            </div>
            <h3 v-if="loiterKeyword && !loiterAlarmsFound.length">
                {{ $t('common.searchNoResult', { val: loiterKeyword }) }}
            </h3>
        </div>
    </div>
</template>

<script type="text/babel">
import Multiselect from 'moxa-vue-multiselect';
import {
    minimumLength,
    maximumNameLength,
    forbidden,
    forbiddenNames,
    maximumCommentLength,
    errorDisplay,
} from '../../../validators';
import {
    DEFAULT_DELAY,
    DEFAULT_DURATION,
    MAX_COMMENT_LENGTH,
    MAX_DELAY_DURATION,
    MAX_NAME_LENGTH,
    MIN_DELAY_DURATION,
    ROUTE_NAMES,
} from '../../../constants';
import numeric from 'vuelidate/lib/validators/numeric';
import minValue from 'vuelidate/lib/validators/minValue';
import maxValue from 'vuelidate/lib/validators/maxValue';
import mappingState from '../../common/mappingState';
import SelectBox from '../../common/SelectBox.vue';
import Hide from '../../common/Hide.vue';
import { sortLocations } from '../../common/helpers';
import cloneDeep from 'lodash-es/cloneDeep';
import intersectionBy from 'lodash-es/intersectionBy';
import invoke from 'lodash-es/invoke';
import findIndex from 'lodash-es/findIndex';

export default {
    components: {
        Multiselect,
        SelectBox,
        Hide,
    },

    mixins: [mappingState],

    constants: {
        MAX_NAME_LENGTH,
        MAX_COMMENT_LENGTH,
        MIN_DELAY_DURATION,
        MAX_DELAY_DURATION,
    },

    props: {
        value: {
            type: [Boolean, Object],
            required: true,
        },

        exitDetectionOptions: {
            type: Array,
            required: true,
        },

        alwaysAccessOptions: {
            type: Array,
            required: true,
        },

        conditionalAccessOptions: {
            type: Array,
            required: true,
        },

        wanderAlarmOptions: {
            type: Array,
            required: true,
        },

        loiterAlarmOptions: {
            type: Array,
            required: true,
        },

        forbiddenNames: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            selected: this.$t('wanderManagement.accessGroupsTab.selected'),
            emptyLabel: '',
            isFixed: false,
            exitDetectionQuery: '',
            alwaysAccessQuery: '',
            conditionalAccessQuery: '',
            wanderVisibility: true,
            loiterVisibility: true,
            wanderKeyword: '',
            loiterKeyword: '',
            infinityPlaceholder: '\u221E',
        };
    },

    computed: {
        /**
         * Returns list of wander alarms for section applying filter
         * @returns array
         */
        wanderAlarmsFound() {
            let keyword = this.wanderKeyword.trim().toLowerCase();
            let newAlarmRow = {
                value: null,
                name: null,
                duration: DEFAULT_DURATION,
                title: null,
            };
            return keyword && !!this.value.wander_alarm && this.value.wander_alarm.length > 0
                ? intersectionBy(
                      this.value.wander_alarm.filter(item => item.name.toLowerCase().includes(keyword)),
                      this.wanderAlarmOptions,
                      'value'
                  )
                : [...intersectionBy(this.value.wander_alarm, this.wanderAlarmOptions, 'value'), newAlarmRow];
        },

        /**
         * Returns list of loiter alarms for section applying filter
         * @returns array
         */
        loiterAlarmsFound() {
            let keyword = this.loiterKeyword.trim().toLowerCase();

            let newAlarmRow = {
                value: null,
                name: null,
                delay: DEFAULT_DELAY,
                duration: DEFAULT_DURATION,
                title: null,
            };

            return keyword && !!this.value.loiter_alarm && this.value.loiter_alarm.length > 0
                ? intersectionBy(
                      this.value.loiter_alarm.filter(item => item.name.toLowerCase().includes(keyword)),
                      this.loiterAlarmOptions,
                      'value'
                  )
                : [...intersectionBy(this.value.loiter_alarm, this.loiterAlarmOptions, 'value'), newAlarmRow];
        },

        /**
         * Returns list of alwaysAccessOptions accordingly to existing the same options
         * in conditionalAccesOptions
         * @returns array
         */
        updatedAlwaysAccessOptions() {
            return this.updateAccessOptionsAccordingly(
                this.alwaysAccessOptions,
                this.value.conditional_access,
                this.$t('wanderManagement.accessGroupsTab.accessGroup.alreadySelected', {
                    val: this.$t('wanderManagement.accessGroupsTab.accessGroup.conditionalAccess'),
                })
            );
        },

        /**
         * Returns list of conditionalAccessOptions accordingly to existing the same options
         * in alwaysAccesOptions
         * @returns array
         */
        updatedConditionalAccessOptions() {
            return this.updateAccessOptionsAccordingly(
                this.conditionalAccessOptions,
                this.value.always_access,
                this.$t('wanderManagement.accessGroupsTab.accessGroup.alreadySelected', {
                    val: this.$t('wanderManagement.accessGroupsTab.accessGroup.alwaysAccess'),
                })
            );
        },
    },

    watch: {
        value: {
            handler() {
                this.setFormValid(!this.$v.validationGroup.$invalid);
            },
            deep: true,
        },

        '$route.path'() {
            // Hide drop down in each Multiselect when going back/forward in history
            [
                'alwaysAccessMultiselect',
                'conditionalAccessMultiselect',
                'exitDetectionMultiselect',
            ].forEach(ref => invoke(this.$refs, ref + '.deactivate'));

            if (this.$router.currentRoute.name === ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__ADD) {
                this.$refs.groupName.select();
            }
        },
    },

    mounted() {
        this.$watch('$v.validationGroup.$invalid', value => {
            this.setFormValid(!value);
        });

        if (this.$router.currentRoute.name === ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__ADD) {
            this.$refs.groupName.select();
        }
    },

    validations() {
        let durationValidation = {
            numeric,
            minValue: minValue(MIN_DELAY_DURATION),
            maxValue: maxValue(MAX_DELAY_DURATION),
        };
        let delayValidation = {
            minimumLength,
            numeric,
            minValue: minValue(MIN_DELAY_DURATION),
            maxValue: maxValue(MAX_DELAY_DURATION),
        };

        return {
            value: {
                name: {
                    minimumLength,
                    maximumNameLength,
                    forbidden,
                    forbiddenNames: (val, rule) => {
                        rule = this.forbiddenNames;
                        return forbiddenNames(val, rule);
                    },
                },
                comment: { maximumCommentLength, forbidden },
                // wander_alarm and loiter_alarm must be validated for common form validation
                wander_alarm: {
                    $each: {
                        duration: durationValidation,
                    },
                },
                loiter_alarm: {
                    $each: {
                        duration: durationValidation,
                        delay: delayValidation,
                    },
                },
            },

            wanderAlarmsFound: {
                $each: {
                    duration: durationValidation,
                },
            },

            loiterAlarmsFound: {
                $each: {
                    duration: durationValidation,
                    delay: delayValidation,
                },
            },

            validationGroup: [
                'value.name',
                'value.comment',
                'value.wander_alarm',
                'value.loiter_alarm',
                'wanderAlarmsFound',
                'loiterAlarmsFound',
            ],
        };
    },

    methods: {
        errorDisplay,

        isDelayDurationValid(alarm, i, field) {
            return (
                !this[alarm + 'AlarmsFound'][i].value ||
                !this.$v[alarm + 'AlarmsFound'].$each[i][field].$invalid
            );
        },

        /**
         * Updates list of options in multiselects' search
         * @param query
         */
        updateAlwaysAccessQuery(query) {
            this.alwaysAccessQuery = query;
        },

        updateConditionalAccessQuery(query) {
            this.conditionalAccessQuery = query;
        },

        updateExitDetectionQuery(query) {
            this.exitDetectionQuery = query;
        },

        update(field, item) {
            this.$emit('input', { ...this.value, [field]: item });
        },

        /**
         * Emits event for updating wander or loiter alarms.
         * Updates specified location with configuration but sends whole alarms array
         * @param field string 'wander' or 'loiter'
         * @param location specific object from alarms array (identified by location id)
         * @param item string new value for alarm configuration
         * @param interval string 'duration' or 'delay' or undefined(in case of adding new location or changing location field)
         */
        updateAlarm(field, location, item, interval) {
            let alarms = cloneDeep(this.value[field + '_alarm']) || [];
            let index = alarms.some(alarm => alarm.value === location.value)
                ? findIndex(alarms, alarm => alarm.value === location.value)
                : alarms.length;

            if (interval) {
                if (!alarms[index]) {
                    return;
                }
                alarms[index][interval] = item;
            } else {
                let locFromOptions = this[field + 'AlarmOptions'].find(loc => loc.value === item);
                locFromOptions.duration = DEFAULT_DURATION;
                if (!alarms[index]) {
                    alarms.push(locFromOptions);
                } else {
                    alarms[index].value = item;
                    alarms[index].name = locFromOptions.name;
                    alarms[index].title = locFromOptions.title;
                }
            }

            this.$emit('input', { ...this.value, [field + '_alarm']: alarms });
        },

        /**
         * Emits event for updating wander or loiter alarms.
         * Removes specified location but sends whole alarms array
         * @param field string 'wander' or 'loiter'
         * @param location specific object from alarms array (identified by location id)
         */
        removeAlarm(field, location) {
            let alarms = cloneDeep(this.value[field + '_alarm']) || [];
            let index = alarms.some(alarm => alarm.value === location.value)
                ? findIndex(alarms, alarm => alarm.value === location.value)
                : alarms.length;

            alarms.splice(index, 1);
            this[field + 'Keyword'] = '';

            this.$emit('input', { ...this.value, [field + '_alarm']: alarms });
        },

        /**
         * Returns list of available locations for specific drop-down list.
         * Drop-down list must not contain already selected locations.
         * @param field string 'wander' or 'loiter'
         * @param index number of row in wander or loiter section
         * @param options all possible locations
         */
        locationOptions(field, index, options) {
            let filteredLocations = options.filter(
                opt => !(this.value[field + '_alarm'] || []).some(loc => loc.value === opt.value)
            );

            if (!!this.value[field + '_alarm'] && !!this.value[field + '_alarm'][index]) {
                filteredLocations.push(this[field + 'AlarmsFound'][index]);
            }
            return sortLocations(filteredLocations);
        },

        /**
         * Returns updated accessOptions (always or conditional) accordingly to existing
         * options in both list.
         * The one option should not be in both lists at the same time,
         * and it must be disabled in one of them
         * @param updating array alwaysAccesOptions or conditionalAccessOptions
         * @param accordingWith array value.conditional_access or value.always_access
         */
        updateAccessOptionsAccordingly(updating, accordingWith, tooltipAddition) {
            let updatedOptions = updating.map(x => {
                if (accordingWith && accordingWith.find(y => y.name === x.name)) {
                    return {
                        ...x,
                        title: x.title + '\n' + tooltipAddition,
                        $isDisabled: true,
                    };
                }
                return { ...x };
            });
            return updatedOptions;
        },
    },
};
</script>
